import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import {
  usePaymentBanking,
  usePaymentBankingStore,
} from "hooks";
import {
  PaymentBankingForm,
  PaymentBankingQuery,
  PaymentBankingRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable";
import { Plus } from "lucide-react";

const todayString = () => new Date().toISOString().slice(0, 10);

const PaymentBankings: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<PaymentBankingRow | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<PaymentBankingQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<"add" | "edit" | undefined>(
    undefined,
  );
  const [selectedBankingId, setSelectedBankingId] = useState<number | string | "pinned" | null>(null);
  const [itemDrafts, setItemDrafts] = useState<PaymentBankingRow["items"]>([]);
  const [confirmRow, setConfirmRow] = useState<PaymentBankingRow | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = usePaymentBankingStore();
  const selectedBanking = useMemo(() => {
    if (selectedBankingId === "pinned") return addRowRef.current;
    if (!rows.length || selectedBankingId === null) return null;
    return rows.find((r) => String(r.id) === String(selectedBankingId)) ?? null;
  }, [rows, selectedBankingId]);

  const {
    fetchList,
    createPaymentBanking,
    updatePaymentBanking,
    deletePaymentBanking,
  } = usePaymentBanking();

  const {
    data: paymentBankingData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const parseNumber = useCallback((value: unknown) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const normalizeAttachments = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : String(item ?? "")))
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item : String(item ?? "")))
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } catch {
        // ignore invalid JSON strings
      }

      return trimmed
        .split(/[,\\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }, []);

  const sanitizeItems = useCallback(
    (items?: PaymentBankingRow["items"]) =>
      Array.isArray(items)
        ? items
          .map((item) => ({
            amount: parseNumber(item.amount),
            partner_id:
              item.partner_id !== undefined &&
                item.partner_id !== null &&
                item.partner_id !== ""
                ? Number(item.partner_id)
                : undefined,
            expense_reason_id:
              item.expense_reason_id !== undefined &&
                item.expense_reason_id !== null &&
                item.expense_reason_id !== ""
                ? Number(item.expense_reason_id)
                : undefined,
            description: item.description?.toString().trim() || undefined,
          }))
          .filter(
            (item) =>
              item.amount ||
              item.partner_id ||
              item.expense_reason_id ||
              Boolean(item.description),
          )
        : [],
    [parseNumber],
  );

  const withItemFields = useCallback(
    (banking: PaymentBankingRow): PaymentBankingRow => {
      const normalizedItems = sanitizeItems(banking.items);
      const firstItem = normalizedItems[0];
      return {
        ...banking,
        attachments: normalizeAttachments(banking.attachments),
        items: normalizedItems,
        amount:
          banking.amount ??
          (typeof firstItem?.amount === "number"
            ? firstItem.amount
            : firstItem?.amount
              ? Number(firstItem.amount)
              : undefined),
        partner_id: banking.partner_id ?? firstItem?.partner_id,
        expense_reason_id:
          banking.expense_reason_id ?? firstItem?.expense_reason_id,
        item_description: banking.item_description ?? firstItem?.description,
      };
    },
    [normalizeAttachments, sanitizeItems],
  );

  useEffect(() => {
    if (paymentBankingData?.data) {
      const mapped =
        (paymentBankingData.data as PaymentBankingRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [paymentBankingData, setRows, withItemFields]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createPaymentBanking.isPending ||
        updatePaymentBanking.isPending ||
        deletePaymentBanking.isPending,
      ),
    );
  }, [
    isFetching,
    createPaymentBanking.isPending,
    updatePaymentBanking.isPending,
    deletePaymentBanking.isPending,
    setLoading,
  ]);

  useEffect(() => {
    if (selectedBankingId === "pinned") {
      setItemDrafts(addRowRef.current?.items ?? []);
      return;
    }
    const found =
      selectedBankingId !== null
        ? rows.find((r) => String(r.id) === String(selectedBankingId))
        : null;
    const next = found ?? rows[0] ?? null;
    setSelectedBankingId(next ? next.id : null);
    setItemDrafts(next?.items ?? []);
  }, [rows, selectedBankingId]);

  const columns = useMemo<ColDef<PaymentBankingRow>[]>(() => {
    return [
      { headerName: "Mã phiếu", field: "payment_code", minWidth: 140, filter: "agTextColumnFilter" },
      { headerName: "Ngày chi", field: "payment_date", minWidth: 140, filter: "agDateColumnFilter" },
      { headerName: "Tài khoản NH", field: "bank_account_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Ngân hàng", field: "bank_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Người nộp", field: "trader_name", flex: 1, minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Địa chỉ người nộp", field: "trader_address", flex: 1, minWidth: 180, filter: "agTextColumnFilter" },
      { headerName: "Người nhận", field: "receiver_name", flex: 1, minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Địa chỉ nhận", field: "receiver_address", flex: 1, minWidth: 180, filter: "agTextColumnFilter" },
      { headerName: "Tài khoản nhận", field: "receiver_account", minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Ngân hàng nhận", field: "receiver_bank", minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Hóa đơn nhập", field: "import_invoice_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Mô tả", field: "description", flex: 1, minWidth: 200, filter: "agTextColumnFilter" },
      { headerName: "Tệp đính kèm", field: "attachments", minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Số tiền", field: "amount", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Đối tác", field: "partner_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Lý do chi", field: "expense_reason_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Mô tả chi tiết", field: "item_description", flex: 1, minWidth: 200, filter: "agTextColumnFilter" },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<PaymentBankingRow>): PaymentBankingForm => {
      const normalizedItems = sanitizeItems(row.items);
      const firstItem = normalizedItems[0];

      const amount = parseNumber(
        row.amount ?? firstItem?.amount ?? row.items?.[0]?.amount,
      );
      const partnerId =
        row.partner_id ??
        firstItem?.partner_id ??
        row.items?.[0]?.partner_id ??
        undefined;
      const expenseReasonId =
        row.expense_reason_id ??
        firstItem?.expense_reason_id ??
        row.items?.[0]?.expense_reason_id ??
        undefined;
      const detail =
        row.item_description ??
        firstItem?.description ??
        row.items?.[0]?.description ??
        "";
      const attachments = normalizeAttachments(row.attachments);

      const importInvoice =
        row.import_invoice_id !== undefined &&
          row.import_invoice_id !== null &&
          row.import_invoice_id !== ""
          ? Number(row.import_invoice_id)
          : undefined;

      const items =
        normalizedItems.length > 0
          ? normalizedItems
          : amount || partnerId || expenseReasonId || detail
            ? [
              {
                amount,
                partner_id: partnerId ? Number(partnerId) : undefined,
                expense_reason_id: expenseReasonId
                  ? Number(expenseReasonId)
                  : undefined,
                description: detail ? String(detail).trim() : undefined,
              },
            ]
            : [];

      return {
        payment_code: row.payment_code ? String(row.payment_code).trim() : "",
        payment_date: String(row.payment_date ?? "").trim(),
        bank_account_id: row.bank_account_id ?? "",
        bank_id: row.bank_id ?? "",
        trader_name: String(row.trader_name ?? "").trim(),
        trader_address: row.trader_address ? String(row.trader_address).trim() : "",
        receiver_name: String(row.receiver_name ?? "").trim(),
        receiver_address: row.receiver_address
          ? String(row.receiver_address).trim()
          : "",
        receiver_account: row.receiver_account
          ? String(row.receiver_account).trim()
          : "",
        receiver_bank: row.receiver_bank ? String(row.receiver_bank).trim() : "",
        description: row.description ? String(row.description).trim() : "",
        attachments,
        import_invoice_id: importInvoice,
        items,
      };
    },
    [normalizeAttachments, parseNumber, sanitizeItems],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      payment_code: "",
      payment_date: todayString(),
      bank_account_id: "",
      bank_id: "",
      trader_name: "",
      trader_address: "",
      receiver_name: "",
      receiver_address: "",
      receiver_account: "",
      receiver_bank: "",
      import_invoice_id: undefined,
      description: "",
      attachments: [],
      amount: undefined,
      partner_id: undefined,
      expense_reason_id: undefined,
      item_description: "",
      items: [],
    } as PaymentBankingRow;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<PaymentBankingRow>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.payment_date || !payload.trader_name || !payload.receiver_name) {
        SnackbarUtils.error("Ngày chi, Người nộp và Người nhận là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      if (!payload.items?.length || !payload.items[0].amount) {
        SnackbarUtils.error("Vui lòng nhập số tiền chi");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createPaymentBanking.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as PaymentBankingRow));
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm phiếu chi ngân hàng không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được phiếu chi cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updatePaymentBanking.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as PaymentBankingRow));
            } else {
              upsertRow(
                withItemFields({ ...(row as PaymentBankingRow), ...payload }),
              );
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật phiếu chi ngân hàng không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createPaymentBanking,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updatePaymentBanking,
      upsertRow,
      withItemFields,
    ],
  );

  const handleSaveRow = useCallback(
    (row: PaymentBankingRow, meta?: { pinned?: boolean }) => {
      const mergedRow =
        meta?.pinned || !row.id
          ? ({
            ...row,
            items: sanitizeItems(itemDrafts),
          } as PaymentBankingRow)
          : row;

      if (meta?.pinned) {
        addRowRef.current = mergedRow;
      }

      persistRow(mergedRow, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
        onSuccess: () => {
          setShowInlineAddRow(undefined);
          setEditingId(null);
        },
      });
    },
    [itemDrafts, persistRow, sanitizeItems, setEditingId],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
    },
    [setEditingId],
  );

  const handleRowClick = useCallback((event: RowClickedEvent<PaymentBankingRow>) => {
    if (event.node?.rowPinned) {
      setSelectedBankingId("pinned");
      setItemDrafts(addRowRef.current?.items ?? []);
      return;
    }
    setSelectedBankingId(event.data?.id ?? null);
    setItemDrafts(event.data?.items ?? []);
  }, []);

  const handleDeleteRow = useCallback((row: PaymentBankingRow) => {
    setConfirmRow(row);
    setShowInlineAddRow(undefined);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePaymentBanking.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu chi ngân hàng không thành công");
      },
    });
  }, [
    confirmRow,
    deletePaymentBanking,
    editingId,
    removeRow,
    setEditingId,
  ]);

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = prev ? undefined : "add";
      if (next) {
        resetInlineAddRow();
        setSelectedBankingId("pinned");
        setItemDrafts(addRowRef.current?.items ?? []);
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
        if (selectedBankingId === "pinned") {
          setSelectedBankingId(rows[0]?.id ?? null);
          setItemDrafts(rows[0]?.items ?? []);
        }
      }
      return next;
    });
  }, [resetInlineAddRow, rows, selectedBankingId, setEditingId]);

  const handleAddItem = useCallback(() => {
    if (!selectedBanking && selectedBankingId !== "pinned") return;
    setItemDrafts((prev) => [
      ...prev,
      { amount: 0, partner_id: "", expense_reason_id: "", description: "" },
    ]);
  }, [selectedBanking, selectedBankingId]);

  const handleUpdateItem = useCallback(
    (index: number, key: "partner_id" | "expense_reason_id" | "description" | "amount", value: string | number) => {
      setItemDrafts((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [key]: key === "amount" ? parseNumber(value) : value,
              }
            : item,
        ),
      );
    },
    [parseNumber],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItemDrafts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveItems = useCallback(() => {
    if (!selectedBanking && selectedBankingId !== "pinned") return;
    const normalizedItems = sanitizeItems(itemDrafts);
    if (!normalizedItems.length) {
      SnackbarUtils.error("Vui lòng thêm ít nhất 1 dòng chi tiết");
      return;
    }

    if (selectedBankingId === "pinned") {
      addRowRef.current = withItemFields({
        ...(addRowRef.current ?? ({} as PaymentBankingRow)),
        items: normalizedItems,
      });
      setItemDrafts(normalizedItems);
      SnackbarUtils.success("Đã lưu chi tiết cho phiếu chi NH mới");
      return;
    }

    if (!selectedBanking?.id) return;

    const mergedRow = withItemFields({
      ...selectedBanking,
      items: normalizedItems,
    } as PaymentBankingRow);

    const payload = normalizePayload(mergedRow);
    updatePaymentBanking.mutate(
      { id: selectedBanking.id, ...payload },
      {
        onSuccess: (res) => {
          const next =
            res?.data && (res.data as PaymentBankingRow)
              ? withItemFields(res.data as PaymentBankingRow)
              : mergedRow;
          upsertRow(next);
          setItemDrafts(next.items ?? []);
          SnackbarUtils.success("Đã lưu chi tiết phiếu chi ngân hàng");
        },
        onError: () => {
          SnackbarUtils.error("Lưu chi tiết phiếu chi ngân hàng không thành công");
        },
      },
    );
  }, [
    itemDrafts,
    normalizePayload,
    sanitizeItems,
    selectedBanking,
    selectedBankingId,
    updatePaymentBanking,
    upsertRow,
    withItemFields,
  ]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  const totalDraftAmount = useMemo(
    () =>
      itemDrafts.reduce(
        (sum, item) => sum + (Number(item.amount ?? 0) || 0),
        0,
      ),
    [itemDrafts],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Accounting] Payment Banking
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý phiếu chi ngân hàng: tạo mới, chỉnh sửa nhanh trên bảng và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {selectedBanking
                  ? `Chi tiết phiếu chi NH${selectedBanking.payment_code ? ` #${selectedBanking.payment_code}` : ""
                  }`
                  : "Chi tiết phiếu chi NH"}
              </p>
              <p className="text-xs text-gray-500">
                {selectedBanking
                  ? `${selectedBanking.trader_name || "Chưa có người nộp"} • ${selectedBanking.payment_date || ""
                  }`
                  : "Chọn một phiếu chi để xem chi tiết."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!selectedBanking && selectedBankingId !== "pinned"}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                Thêm dòng
              </button>
              {selectedBankingId !== "pinned" && (
                <button
                  type="button"
                  onClick={handleSaveItems}
                  disabled={!selectedBanking || updatePaymentBanking.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                >
                  {updatePaymentBanking.isPending ? "Đang lưu..." : "Lưu chi tiết"}
                </button>
              )}
              <div className="text-right">
                <p className="text-xs text-gray-500">Tổng tiền</p>
                <p className="text-sm font-semibold text-gray-900">
                  {totalDraftAmount.toLocaleString("vi-VN")}
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">#</th>
                  <th className="px-4 py-2 text-left font-semibold">Đối tác</th>
                  <th className="px-4 py-2 text-left font-semibold">Lý do chi</th>
                  <th className="px-4 py-2 text-left font-semibold">Mô tả</th>
                  <th className="px-4 py-2 text-right font-semibold">Số tiền</th>
                  <th className="px-4 py-2 text-center font-semibold">Hành động</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {itemDrafts.length ? (
                  itemDrafts.map((item, index) => (
                    <tr key={`${selectedBanking?.id ?? "draft"}-item-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.partner_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "partner_id", e.target.value)
                          }
                          disabled={!selectedBanking && selectedBankingId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Mã đối tác"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.expense_reason_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(
                              index,
                              "expense_reason_id",
                              e.target.value,
                            )
                          }
                          disabled={!selectedBanking && selectedBankingId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Lý do chi"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        <input
                          value={item.description ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "description", e.target.value)
                          }
                          disabled={!selectedBanking && selectedBankingId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Mô tả"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.amount ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "amount", e.target.value)
                          }
                          disabled={!selectedBanking && selectedBankingId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={!selectedBanking && selectedBankingId !== "pinned"}
                          className="text-red-600 hover:text-red-700 text-xs font-semibold disabled:opacity-60"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-4 text-center text-gray-500" colSpan={6}>
                      {selectedBanking || selectedBankingId === "pinned"
                        ? 'Chưa có dòng chi tiết. Bấm "Thêm dòng" để bắt đầu.'
                        : "Chọn một phiếu chi để xem chi tiết."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu chi ngân hàng
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4 text-green-700" />
              {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm phiếu chi NH"}
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={paymentBankingData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow(undefined);
            setEditingId(null);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu chi, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu chi ngân hàng?"
        description={`Bạn chắc chắn muốn xóa phiếu chi ngân hàng "${confirmRow?.payment_code ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePaymentBanking.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PaymentBankings;
